import React, { useEffect, useState, Suspense, lazy, createElement } from 'react';
// Remove the meta-pixel import and add our direct Facebook Pixel component
import FacebookPixel, { trackPixelEvent } from './components/FacebookPixel';
// Only import the Header component eagerly - everything else is lazy loaded
const Header = lazy(() => import('./components/Header').then(module => ({
  default: module.Header
})));
// Preload critical images with proper priority
const preloadCriticalImages = () => {
  const imagesToPreload = [
  // Video thumbnail - highest priority
  'https://uploadthingy.s3.us-west-1.amazonaws.com/4cqh5dBrBeuDmGb8oS2AtQ/thumb.webp',
  // Product images - medium priority
  'https://uploadthingy.s3.us-west-1.amazonaws.com/hD896AedmRAgy88fjtpFSh/ChatGPT_Image_3_%CE%91%CF%85%CE%B3_2025__03_09_55_%CE%BC.%CE%BC.-removebg-preview.webp', 'https://uploadthingy.s3.us-west-1.amazonaws.com/efA9ZtbNd4Rt7WVbG15E4f/ChatGPT_Image_4_%CE%91%CF%85%CE%B3_2025__01_01_21_%CE%BC.%CE%BC.-removebg-preview.webp'];
  // Create a map of preloaded images to track status
  const preloadedImages = new Map();
  // Preload images with proper priority and loading attributes
  imagesToPreload.forEach((src, index) => {
    const img = new Image();
    img.src = src;
    img.fetchPriority = index === 0 ? 'high' : 'low';
    img.loading = index === 0 ? 'eager' : 'lazy';
    // Store in our map for reference
    preloadedImages.set(src, img);
  });
  // Return the map in case we want to check status later
  return preloadedImages;
};
// Preconnect to external domains to speed up resource fetching
const setupResourceHints = () => {
  const domains = ['https://uploadthingy.s3.us-west-1.amazonaws.com', 'https://player.vimeo.com', 'https://images.unsplash.com', 'https://connect.facebook.net' // Add Facebook domain for pixel
  ];
  domains.forEach(domain => {
    const link = document.createElement('link');
    link.rel = 'preconnect';
    link.href = domain;
    link.crossOrigin = 'anonymous';
    document.head.appendChild(link);
    // Also add DNS prefetch as fallback for browsers that don't support preconnect
    const dnsLink = document.createElement('link');
    dnsLink.rel = 'dns-prefetch';
    dnsLink.href = domain;
    document.head.appendChild(dnsLink);
  });
};
// Detect mobile device
const isMobileDevice = () => {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
};
// Dynamically import components with aggressive code splitting and mobile-specific chunks
const getProductOptions = () => {
  return import('./components/ProductOptions').then(module => ({
    default: module.ProductOptions
  }));
};
// Use smaller chunks for mobile
const getBenefitsSection = () => {
  return import('./components/BenefitsSection').then(module => ({
    default: module.BenefitsSection
  }));
};
// Wrap each component in a separate chunk
const ProductOptions = lazy(getProductOptions);
const BenefitsSection = lazy(getBenefitsSection);
const TestimonialsSection = lazy(() => import('./components/TestimonialsSection').then(module => ({
  default: module.TestimonialsSection
})));
const ProductDetails = lazy(() => import('./components/ProductDetails').then(module => ({
  default: module.ProductDetails
})));
const ExpertSection = lazy(() => import('./components/ExpertSection').then(module => ({
  default: module.ExpertSection
})));
const MoreTestimonials = lazy(() => import('./components/MoreTestimonials').then(module => ({
  default: module.MoreTestimonials
})));
const Footer = lazy(() => import('./components/Footer').then(module => ({
  default: module.Footer
})));
const StickyAddToCart = lazy(() => import('./components/StickyAddToCart').then(module => ({
  default: module.StickyAddToCart
})));
// Custom loading component that's very lightweight
const LoadingFallback = ({
  height = '20',
  isImportant = false
}) => <div className={`w-full h-${height} bg-gray-100 ${isImportant ? 'flex items-center justify-center' : 'animate-pulse'}`} data-id="element-0">
    {isImportant && <div className="w-8 h-8 border-t-2 border-b-2 border-blue-500 rounded-full animate-spin" data-id="element-1"></div>}
  </div>;
export function App() {
  const [isMobile, setIsMobile] = useState(false);
  // State to control all sections visibility based on video progress
  const [showAllSections, setShowAllSections] = useState(false);
  // Initialize Facebook Pixel with ViewContent event
  useEffect(() => {
    try {
      // Track that the user has viewed the landing page
      trackPixelEvent('ViewContent', {
        content_name: 'Hair Growth Product Landing Page',
        content_category: 'Hair Care'
      });
      // Define the purchase tracking function
      const trackPurchaseClick = (productName, value) => {
        trackPixelEvent('InitiateCheckout', {
          content_name: productName,
          currency: 'USD',
          value: value
        });
      };
      // Expose the tracking function to window for use in other components
      window.trackPurchaseClick = trackPurchaseClick;
      // Clean up function
      return () => {
        // Remove any global handlers if needed
        delete window.trackPurchaseClick;
      };
    } catch (error) {
      console.error('Facebook Pixel error:', error);
      // Provide a dummy function to prevent errors in other components
      window.trackPurchaseClick = () => {};
      return () => {
        delete window.trackPurchaseClick;
      };
    }
  }, []);
  // Detect mobile on mount
  useEffect(() => {
    setIsMobile(isMobileDevice());
    // Add resize listener to update mobile status
    const handleResize = () => {
      setIsMobile(isMobileDevice());
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  // Set up performance optimizations when component mounts
  useEffect(() => {
    // Preload critical images
    const images = preloadCriticalImages();
    // Set up resource hints for faster external resource loading
    setupResourceHints();
    // Set default volume in localStorage to 1 to ensure sound is enabled
    localStorage.setItem('vimeoVideoVolume', '1');
    // Preload Vimeo API script immediately on mobile
    if (isMobile) {
      const script = document.createElement('script');
      script.src = 'https://player.vimeo.com/api/player.js';
      script.async = true;
      document.body.appendChild(script);
    } else {
      // On desktop, load after a short delay
      const timer = setTimeout(() => {
        const script = document.createElement('script');
        script.src = 'https://player.vimeo.com/api/player.js';
        script.async = true;
        document.body.appendChild(script);
      }, 1000);
      return () => clearTimeout(timer);
    }
    // Preload critical components for better performance
    if (isMobile) {
      // Preload first components on mobile for better performance
      getProductOptions();
      getBenefitsSection();
    }
    // Add viewport meta tag optimization for mobile
    if (isMobile) {
      const viewportMeta = document.querySelector('meta[name="viewport"]');
      if (viewportMeta) {
        viewportMeta.setAttribute('content', 'width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no');
      } else {
        const meta = document.createElement('meta');
        meta.name = 'viewport';
        meta.content = 'width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no';
        document.head.appendChild(meta);
      }
    }
  }, [isMobile]);
  // Enhanced video progress checking with storage event listener
  useEffect(() => {
    const checkVideoProgress = () => {
      const hasReachedTimestamp = localStorage.getItem('videoReached1350');
      if (hasReachedTimestamp === 'true') {
        console.log('Showing all sections');
        setShowAllSections(true);
      }
    };
    // Check initially
    checkVideoProgress();
    // Listen for storage events (triggered when localStorage changes)
    const handleStorageChange = () => {
      checkVideoProgress();
    };
    window.addEventListener('storage', handleStorageChange);
    // Also set up interval as backup
    const interval = setInterval(checkVideoProgress, 500);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(interval);
    };
  }, []);
  return <div className="flex flex-col w-full min-h-full bg-white pb-16" data-id="element-2">
      {/* Add the Facebook Pixel component */}
      <FacebookPixel data-id="element-3" />
      <Suspense fallback={<LoadingFallback height="64" isImportant={true} data-id="element-5" />} data-id="element-4">
        <Header data-id="element-6" />
      </Suspense>
      {/* Show all sections only after video reaches 13:50 */}
      {showAllSections && <>
          <Suspense fallback={<LoadingFallback height="64" isImportant={true} data-id="element-8" />} data-id="element-7">
            <ProductOptions data-id="element-9" />
          </Suspense>
          <Suspense fallback={<LoadingFallback height="20" data-id="element-11" />} data-id="element-10">
            <BenefitsSection data-id="element-12" />
          </Suspense>
          <Suspense fallback={<LoadingFallback height="20" data-id="element-14" />} data-id="element-13">
            <TestimonialsSection data-id="element-15" />
          </Suspense>
          <Suspense fallback={<LoadingFallback height="20" data-id="element-17" />} data-id="element-16">
            <ProductDetails data-id="element-18" />
          </Suspense>
          <Suspense fallback={<LoadingFallback height="20" data-id="element-20" />} data-id="element-19">
            <ExpertSection data-id="element-21" />
          </Suspense>
          <Suspense fallback={<LoadingFallback height="20" data-id="element-23" />} data-id="element-22">
            <MoreTestimonials data-id="element-24" />
          </Suspense>
          <Suspense fallback={<LoadingFallback height="20" data-id="element-26" />} data-id="element-25">
            <Footer data-id="element-27" />
          </Suspense>
          <Suspense fallback={null} data-id="element-28">
            <StickyAddToCart data-id="element-29" />
          </Suspense>
        </>}
    </div>;
}